import {
  useEffect,
  useState,
  Dispatch,
  Fragment,
  MouseEvent,
  SetStateAction,
  type ReactNode,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';

// material-ui
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// third-party
import { FormattedMessage } from 'react-intl';

// project-imports
import NavItem from './NavItem';
import NavCollapse from './NavCollapse';
import SimpleBar from 'components/third-party/SimpleBar';
import Transitions from 'components/@extended/Transitions';

import { useGetMenuMaster } from 'api/menu';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';

// assets
import { More2 } from 'iconsax-reactjs';

// types
import { NavItemType } from 'types/menu';

interface Props {
  item: NavItemType;
  lastItem: number;
  remItems: NavItemType[];
  lastItemId: string;
  selectedID: string | undefined;
  setSelectedID: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedItems: Dispatch<SetStateAction<string | undefined>>;
  selectedItems: string | undefined;
  setSelectedLevel: Dispatch<SetStateAction<number>>;
  selectedLevel: number;
}

type VirtualElement = {
  getBoundingClientRect: () => DOMRect;
  contextElement?: Element;
};

function mergeRemElementsAsChildren(source: NavItemType, remItems: NavItemType[]): NavItemType {
  const nested = remItems.map((e) => e.elements ?? []);
  const children = nested.flat();
  return { ...source, children };
}

type NavGroupMenuHandlers = {
  setSelectedItems: Dispatch<SetStateAction<string | undefined>>;
  setSelectedLevel: Dispatch<SetStateAction<number>>;
  selectedLevel: number;
  selectedItems: string | undefined;
};

function renderNavGroupMenuEntry(
  menu: NavItemType,
  collapseParentId: string,
  handlers: NavGroupMenuHandlers,
  variant: 'group-children' | 'elements-list'
): ReactNode {
  const { setSelectedItems, setSelectedLevel, selectedLevel, selectedItems } = handlers;
  switch (menu.type) {
    case 'collapse':
      return (
        <NavCollapse
          key={menu.id}
          menu={menu}
          setSelectedItems={setSelectedItems}
          setSelectedLevel={setSelectedLevel}
          selectedLevel={selectedLevel}
          selectedItems={selectedItems}
          level={1}
          parentId={collapseParentId}
        />
      );
    case 'item':
      return <NavItem key={menu.id} item={menu} level={1} />;
    default: {
      const errorKey = variant === 'group-children' ? `nav-group-child-unknown-${menu.id}` : menu.id;
      const message = variant === 'group-children' ? 'Fix - Group Collapse or Items' : 'Menu Items Error';
      return (
        <Typography key={errorKey} variant="h6" color="error" align="center">
          {message}
        </Typography>
      );
    }
  }
}

const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 180,
  '&:before': {
    background: theme.palette.background.paper,
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 5,
    left: 32,
    width: 12,
    height: 12,
    transform: 'translateY(-50%) rotate(45deg)',
    zIndex: 120,
    borderWidth: '6px',
    borderStyle: 'solid',
    borderColor: `${theme.palette.background.paper}  transparent transparent ${theme.palette.background.paper}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));

type NavGroupVerticalLayoutProps = {
  currentItem: NavItemType;
  drawerOpen: boolean;
  navCollapse: ReactNode;
};

function NavGroupVerticalLayout({ currentItem, drawerOpen, navCollapse }: Readonly<NavGroupVerticalLayoutProps>) {
  return (
    <>
      {currentItem.title && drawerOpen && (
        <Typography variant="caption" color="secondary" sx={{ px: 3, pt: 2, pb: 0.5, display: 'block', fontWeight: 600 }}>
          {currentItem.title.toUpperCase()}
        </Typography>
      )}
      <List sx={{ mt: drawerOpen ? 0.5 : 0, py: 0, zIndex: 0 }}>{navCollapse}</List>
    </>
  );
}

type NavGroupHorizontalLayoutProps = {
  isSelected: boolean;
  anchorEl: VirtualElement | (() => VirtualElement) | null | undefined;
  openMini: boolean;
  popperId: string | undefined;
  handleClick: (event: MouseEvent<HTMLElement> | undefined) => void;
  handleClose: () => void;
  itemIcon: ReactNode;
  currentItem: NavItemType;
  lastItemId: string;
  moreItems: ReactNode;
  items: ReactNode;
};

function NavGroupHorizontalLayout({
  isSelected,
  anchorEl,
  openMini,
  popperId,
  handleClick,
  handleClose,
  itemIcon,
  currentItem,
  lastItemId,
  moreItems,
  items
}: Readonly<NavGroupHorizontalLayoutProps>) {
  return (
    <List>
      <ListItemButton
        selected={isSelected}
        sx={{ p: 1, px: 1.5, my: 0.5, mr: 1, display: 'flex', alignItems: 'center', borderRadius: 1 }}
        onMouseEnter={handleClick}
        onClick={handleClick}
        onMouseLeave={handleClose}
        disableTouchRipple
        aria-describedby={popperId}
        className={anchorEl ? 'Mui-selected' : ''}
      >
        {itemIcon && (
          <ListItemIcon sx={{ minWidth: 32 }}>
            {currentItem.id === lastItemId ? <More2 size={22} variant="Bulk" /> : itemIcon}
          </ListItemIcon>
        )}
        <ListItemText
          sx={{ mr: 1 }}
          primary={
            <Typography
              variant="h6"
              sx={(theme) => ({
                fontWeight: isSelected || anchorEl ? 500 : 400,
                color: 'secondary.main',
                ...theme.applyStyles('dark', { color: 'secondary.400' }),
                ...((isSelected || anchorEl) && { color: 'primary.main' })
              })}
            >
              <FormattedMessage id={currentItem.id === lastItemId ? 'more-items' : currentItem.title} />
            </Typography>
          }
        />
        {anchorEl && (
          <PopperStyled id={popperId} open={openMini} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 2001 }}>
            {({ TransitionProps }) => (
              <Transitions in={openMini} {...TransitionProps}>
                <Paper
                  sx={(theme) => ({
                    mt: 0.5,
                    py: 1.25,
                    boxShadow: theme.customShadows.z1,
                    border: '1px solid ',
                    borderColor: 'divider',
                    backgroundImage: 'none'
                  })}
                >
                  <ClickAwayListener onClickAway={handleClose}>
                    <SimpleBar
                      sx={{
                        minWidth: 200,
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 170px)'
                      }}
                    >
                      {currentItem.id === lastItemId ? moreItems : items}
                    </SimpleBar>
                  </ClickAwayListener>
                </Paper>
              </Transitions>
            )}
          </PopperStyled>
        )}
      </ListItemButton>
    </List>
  );
}

// ==============================|| NAVIGATION - GROUP ||============================== //

export default function NavGroup({
  item,
  lastItem,
  remItems,
  lastItemId,
  selectedID,
  setSelectedID,
  setSelectedItems,
  selectedItems,
  setSelectedLevel,
  selectedLevel
}: Readonly<Props>) {
  const { pathname } = useLocation();

  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const [anchorEl, setAnchorEl] = useState<VirtualElement | (() => VirtualElement) | null | undefined>(null);
  const [currentItem, setCurrentItem] = useState(item);

  const openMini = Boolean(anchorEl);

  useEffect(() => {
    if (!lastItem) return;
    if (item.id === lastItemId) {
      setCurrentItem(mergeRemElementsAsChildren(item, remItems));
      return;
    }
    setCurrentItem(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, lastItem, downLG]);

  const checkOpenForParent = (child: NavItemType[], id: string | undefined) => {
    child.forEach((ele: NavItemType) => {
      if (ele.children?.length) {
        checkOpenForParent(ele.children, id);
      }

      if (ele.url && !!matchPath({ path: ele?.link ?? ele.url, end: true }, pathname)) {
        setSelectedID(id);
      }
    });
  };
  const checkSelectedOnload = (data: NavItemType) => {
    const childrens = data.children ?? [];
    childrens.forEach((itemCheck: NavItemType) => {
      if (itemCheck?.children?.length) {
        checkOpenForParent(itemCheck.children, currentItem.id);
      }

      if (itemCheck.url && !!matchPath({ path: itemCheck?.link ?? itemCheck.url, end: true }, pathname)) {
        setSelectedID(currentItem.id);
      }
    });
  };

  useEffect(() => {
    checkSelectedOnload(currentItem);
    if (openMini) setAnchorEl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, currentItem]);

  const handleClick = (event: MouseEvent<HTMLElement> | undefined) => {
    if (!openMini) {
      setAnchorEl(event?.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isSelected = selectedID === currentItem.id;

  const GroupIcon = currentItem.icon;
  const itemIcon = GroupIcon ? (
    <Box component="span" sx={{ color: isSelected || anchorEl ? 'primary.main' : 'secondary.main' }}>
      <GroupIcon variant="Bulk" size={22} />
    </Box>
  ) : null;

  const collapseParentId = currentItem.id ?? item.id ?? '';

  const menuHandlers: NavGroupMenuHandlers = {
    setSelectedItems,
    setSelectedLevel,
    selectedLevel,
    selectedItems,
  };

  const navCollapse = item.children?.map((menuItem) =>
    renderNavGroupMenuEntry(menuItem, collapseParentId, menuHandlers, 'group-children')
  );

  const moreItems = remItems.map((itemRem: NavItemType) => (
    <Fragment key={itemRem.id ?? `overflow-${currentItem.id}-${itemRem.title ?? ''}-${itemRem.url ?? ''}`}>
      {itemRem.url ? (
        <NavItem item={itemRem} level={1} />
      ) : (
        itemRem.title && (
          <Typography variant="caption" sx={{ pl: 2 }}>
            {itemRem.title} {itemRem.url}
          </Typography>
        )
      )}
      {itemRem?.elements?.map((menu) => renderNavGroupMenuEntry(menu, collapseParentId, menuHandlers, 'elements-list'))}
    </Fragment>
  ));

  // menu list collapse & items
  const items = currentItem.children?.map((menu) =>
    renderNavGroupMenuEntry(menu, collapseParentId, menuHandlers, 'elements-list')
  );

  const popperId = openMini ? `group-pop-${item.id}` : undefined;

  const isVertical = menuOrientation === MenuOrientation.VERTICAL || downLG;
  if (isVertical) {
    return <NavGroupVerticalLayout currentItem={currentItem} drawerOpen={drawerOpen} navCollapse={navCollapse} />;
  }

  return (
    <NavGroupHorizontalLayout
      isSelected={isSelected}
      anchorEl={anchorEl}
      openMini={openMini}
      popperId={popperId}
      handleClick={handleClick}
      handleClose={handleClose}
      itemIcon={itemIcon}
      currentItem={currentItem}
      lastItemId={lastItemId}
      moreItems={moreItems}
      items={items}
    />
  );
}
